import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';

import { MobileContext } from 'context';
import {
  useCurrentRound, useGameDetails, useNextRound, usePreviousRound, useCurrentPrice,
} from 'utils/Hooks';
import { PopupGeneric } from 'utils/Popups';
import { telegramGroupHyperlink } from 'utils/Misc';
import './styles.scss';

const maxClockSkewThreshold = 45; // seconds
const maxTimeBeforeContractConsideredStuck = 60; // seconds
const loadingScreenAutoRemovalTimeout = 2.5 * 1000;
const executionUpdateInterval = 2 * 1000;


function GameStatus(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useContext(MobileContext);

  const gameDetails = useGameDetails();
  const roundNext = useNextRound();
  const roundCurrent = useCurrentRound();
  const roundPrevious = usePreviousRound();
  const currentPrice = useCurrentPrice();

  const [skewTooSevere, SetSkewTooSevere] = useState(false);
  const [lastSkew, SetLastSkew] = useState(0);
  const [isPaused, SetIsPaused] = useState(false);
  const [isStuck, SetIsStuck] = useState(false);
  const [isExecuting, SetIsExecuting] = useState(false);
  const [isWritingToBlockchain, SetIsWritingToBlockchain] = useState(false);
  const [isLoading, SetIsLoading] = useState(true);
  const [expectedLockPrice, SetExpectedPrice] = useState({ price: 0, roundID: 0 });
  const [isScheduledPause, SetIsScheduledPause] = useState(false);
  const [isInfoPopupShown, SetWhetherShowingInfoPopup] = useState(false);

  useEffect(() => {
    UpdateContractPausedState();
    UpdateTimeSkew();
    const interval = setInterval(() => { UpdateContractPausedState(); UpdateContractPausedState() }, 5000);
    return () => {
      clearInterval(interval);
    }
  }, [gameDetails]);

  useEffect(() => {
    SetIsScheduledPause(DetermineIfScheduled());
    SetIsStuck(IsContractStuck());
    UpdateWritingToBlockchainStatus();
    const interval = setInterval(UpdateWritingToBlockchainStatus, executionUpdateInterval);
    return () => {
      clearInterval(interval);
    }
  }, [roundNext]);

  useEffect(() => {
    UpdateExecutionStatus();
    const interval = setInterval(UpdateExecutionStatus, executionUpdateInterval);
    return () => {
      clearInterval(interval);
    }
  }, [roundCurrent]);

  useEffect(() => {
    if (isExecuting) {
      const savedPriceIsNotForThisRound = (expectedLockPrice.roundID != roundCurrent.epoch);
      if (savedPriceIsNotForThisRound)
        SetExpectedPrice({ price: currentPrice, roundID: roundCurrent.epoch })
    }
  }, [isExecuting, roundCurrent, currentPrice, expectedLockPrice]);


  useEffect(() => {
    SetIsLoading(IsGameLoading());
    const fallbackTimeout = setTimeout(() => SetIsLoading(false), loadingScreenAutoRemovalTimeout);
    return () => {
      clearTimeout(fallbackTimeout);
    }
  }, [roundPrevious, roundCurrent, roundNext]);

  useEffect(UpdateSkewSeverityStatus
    , [lastSkew]); // eslint-disable-line react-hooks/exhaustive-deps


  async function UpdateTimeSkew() {
    if (!gameDetails.contracts.prediction) return;
    SetLastSkew(await GetClockSkewRelativeToBlockchain());
  }

  async function UpdateContractPausedState() {
    if (!gameDetails.contracts.prediction) return;
    const paused = await gameDetails.contracts.prediction.methods.IsPaused().call();
    SetIsPaused(paused);
  }

  function UpdateSkewSeverityStatus() {
    SetSkewTooSevere(ClockSkewTooSevere());
  }
  function UpdateExecutionStatus() {
    SetIsExecuting(IsContractExecuting());
  }
  function UpdateWritingToBlockchainStatus() {
    SetIsWritingToBlockchain(IsAdminPanelWritingToBlockchain());
  }

  function NavigateToHistory() {
    const historyPagePath = location.pathname.replace('prediction', 'history');
    navigate(historyPagePath);
  }

  function DetermineIfScheduled() {
    if (roundNext.canceled)
      return true;
    else
      return false;
  }

  function IsContractStuck() {
    if (!roundNext.lockTimestamp)
      return false;
    const nowTimestampSeconds = (Date.now() / 1000).toFixed(0);
    if (Number(roundNext.lockTimestamp) + maxTimeBeforeContractConsideredStuck < nowTimestampSeconds) {
      return true;
    } else {
      return false;
    }
  }

  function IsContractExecuting() {
    //return true; // debug
    if (!roundCurrent.closeTimestamp)
      return false;
    else if (roundCurrent.closeTimestamp * 1000 < (Date.now() + 1000))
      return true;
    else
      return false;
  }

  function IsAdminPanelWritingToBlockchain() {
    if (!roundNext.lockTimestamp)
      return false;
    else if (roundNext.lockTimestamp * 1000 < (Date.now() + 1000))
      return true;
    else
      return false;
  }

  function IsGameLoading() {
    if (!roundPrevious.startTimestamp || !roundCurrent.startTimestamp || !roundNext.startTimestamp)
      return true;
    else
      return false;
  }

  async function GetClockSkewRelativeToBlockchain() {
    const blockTimestamp = Number(await gameDetails.contracts.prediction.methods.currentBlockTimestamp().call()) * 1000;
    const absoluteLagSeconds = ((blockTimestamp - Date.now()) / 1000).toFixed(0);
    return Math.abs(absoluteLagSeconds);
  }

  function ClockSkewTooSevere() {
    if (lastSkew > maxClockSkewThreshold) {
      console.log('severe');
      console.warn('Severe clock skew detected(behind by ' + lastSkew + ' secs);' +
        ' adjust your computer\'s time');
      return true;
    } else {
      return false;
    }
  }



  function DogeChainWarning() {
    if (gameDetails.wantedChain.name == 'dogechain')
      return (<p>Or DogeChain RPC could be down.</p>);
    else
      return null;
  }

  const clockSkewNoticeM = useMemo(ClockSkewNotice, [lastSkew]);
  function ClockSkewNotice() {
    return (<div className='ClockSkew'>
      <p>Your clock is off by {lastSkew} seconds;</p>
      <p>Either your computer's time is wrong, or this blockchain/its RPCs are overloaded</p>
      <p>If this warning persists, please reach out to us in our {telegramGroupHyperlink}</p>
    </div>);
  }

  const errorNoticeM = useMemo(ErrorNotice,
    [isPaused, isStuck, isScheduledPause, skewTooSevere, lastSkew, gameDetails]);
  function ErrorNotice() {
    /*if (isPaused && isScheduledPause) { // disabled for now, since there seems to be no need for this
      return (<p>Predictions paused.</p>); */
    if (isPaused) {
      return (<p>
        Predictions paused.
      </p>);
    } else if (isStuck) {
      if (skewTooSevere)
        return clockSkewNoticeM;
      else
        return (<div>
          <p>Predictions paused due to technical difficulties.</p>
          {DogeChainWarning()}
        </div>);
    } else if (skewTooSevere) {
      return clockSkewNoticeM;
    } else {
      return null;
    }
  }


  const executionNoticeM = useMemo(ExecutionNotice,
    [roundCurrent, isExecuting, isWritingToBlockchain, expectedLockPrice, isInfoPopupShown]);
  function ExecutionNotice() {
    const executionNotice = (<React.Fragment>
      <PopupGeneric title='Info' isShown={isInfoPopupShown}
        ClosePopup={() => SetWhetherShowingInfoPopup(false)}
      >
        <p>
          This price is shown for reference only.{' '}
          It can differ from what's written to the contract.{' '}
          Check{' '}
          <span className='GoToHistory' onClick={NavigateToHistory}>history page</span>{' '}
          for exact closing price & price feed history
        </p>
      </PopupGeneric>
      <p>Executing contract...</p>
      {expectedLockPrice.price ?
        (<div className='ExpectedLockedPrice'>
          <div className='RoundID'>#{roundCurrent.epoch}</div>
          <p>{expectedLockPrice.price} <span className='USDT'>USDT</span></p>
          <button className='PriceInfo' onClick={() => SetWhetherShowingInfoPopup(true)}>
            <p>Expected price<sup>?</sup></p>
          </button>
        </div>
        ) : (null)
      }
    </React.Fragment>);

    if (isExecuting && isWritingToBlockchain) {
      //if (true) { // debug
      return (<div>
        {executionNotice}
        <p>Writing to blockchain...</p>
      </div>);
    } else if (isExecuting) {
      return executionNotice;
    } else {
      return null;
    }
  }


  const loadingNotice = (<p>Loading...</p>);

  let message = null;
  if (isPaused || isStuck || skewTooSevere) {
    message = (<div className='StatusMessage'>{errorNoticeM}</div>);
  } else if (isLoading) {
    message = (<div className='StatusMessage'>{loadingNotice}</div>);
  } else if (isExecuting) {
    message = (<div className='StatusMessage'>{executionNoticeM}</div>);
  } else {
    message = null;
  }

  if (!message)
    return null;
  else
    return (<React.Fragment>
      <div className={'ContractStatus' + (isMobile ? ' Mobile' : ' ')}>
        {message}
      </div>
    </React.Fragment>);
}

export default GameStatus;
